/* eslint-disable no-console */
import { trace } from '@opentelemetry/api'
import { isString } from '@/helpers/helpers'
import { TraceId, isValidTraceId, makeUniqueId } from '@/logging/traceidUtils'
import { currentVersionName } from '@/config/base'
import { KIBANA_CHAR_LIMIT } from '@/constants/logging'

type SeverityLevel = 'trace' | 'debug' | 'info' | 'warn' | 'error'

interface LogPayload {
  msg: string
  severity: SeverityLevel
  traceId?: TraceId
  spanId?: string
  clientUrl?: string
  userId?: string
  stackTrace?: string
  userAgent?: string
  service?: string
  appVersion?: string
  timestamp?: string // ISO 8601 Extended Format
}

type KibanaSeverity = 'INFO' | 'WARNING' | 'ERROR'
// https://docs.google.com/document/d/12Nx0vu3DvBrTfGo0M8_T-r5hid7oJD3tG8C7-zKKtRk

interface KibanaPayload {
  trace_id?: string
  span_id?: string
  trace_flags?: number
  SPAN?: string
  TRACE?: string
  USER?: string
  env?: string // autofilled by kibana
  service?: string // autofilled by kibana, but can be overriden
  message?: string | object
  stack_trace?: string
  user_agent?: string
  logger?: string
  severity?: KibanaSeverity
  thread?: string
  app_version?: string
  client_url?: string
  '@timestamp'?: string // ISO 8601 Extended Format
}

const getSeverityFromLogPayloadToKibanaPayload = (severity: SeverityLevel): string => {
  switch (severity) {
    case 'error':
    case 'info':
      return severity.toUpperCase()
    case 'warn':
      return 'WARNING'
    default:
      return 'INFO'
  }
}

export const logData = ({
  msg,
  severity = 'info',
  traceId,
  spanId,
  clientUrl,
  userId,
  stackTrace,
  userAgent,
  service,
  appVersion = currentVersionName,
  timestamp = new Date().toISOString()
}: LogPayload): void => {
  const stringifiedMessage = isString(msg) ? msg : JSON.stringify(msg)
  traceId = !isValidTraceId(traceId) ? makeUniqueId() : traceId
  spanId = !isValidTraceId(spanId) ? makeUniqueId() : spanId

  // related to OpenTelemetry
  let activeSpan = trace.getActiveSpan()
  let spanContext = activeSpan?.spanContext()

  const trace_id = spanContext?.traceId
  const span_id = spanContext?.spanId
  const traceFlags = spanContext?.traceFlags
  const kibanaPayload: KibanaPayload = {
    trace_id: trace_id,
    span_id: span_id,
    trace_flags: traceFlags,
    TRACE: traceId,
    SPAN: spanId,
    severity: getSeverityFromLogPayloadToKibanaPayload(severity) as KibanaSeverity,
    logger: 'PublicProjectsSSRNodeLogger',
    service,
    stack_trace: stackTrace,
    user_agent: userAgent,
    USER: userId,
    message: stringifiedMessage,
    app_version: appVersion,
    client_url: clientUrl,
    '@timestamp': timestamp
  }

  // uncomment the line below to dump the json being outputed in the message, for debugging purposes
  // kibanaPayload.message = kibanaPayload.message + ' >>> ' + JSON.stringify(kibanaPayload)

  let stringifiedLog = JSON.stringify(kibanaPayload)
  if (stringifiedLog.length > KIBANA_CHAR_LIMIT) {
    stringifiedLog = JSON.stringify({
      ...kibanaPayload,
      message: stringifiedMessage.slice(0, KIBANA_CHAR_LIMIT - stringifiedLog.length)
      // shorten the message so that the entire thing fits in the kibana limits
    })
  }

  console.log(stringifiedLog)
}
